/**
 * Clicking a date field anywhere opens its calendar (TICK-00097).
 *
 * A native `<input type="date">` — which is what `v-text-field type="date"`
 * renders — only opens its picker when the browser's own tiny indicator is hit.
 * Clicking the digits does nothing, and in our outlined fields that indicator is
 * barely visible, so a date field reads as plain text and the calendar looks
 * broken. The ticket asks for one behaviour across the whole product, and there
 * are ~169 such fields on ~81 screens: fixing them one by one would guarantee
 * the next one is forgotten.
 *
 * So this is a single document-level listener instead. It calls `showPicker()`
 * on the input that was clicked, which is the standard way to open a native
 * picker (Chrome/Edge 99+, Firefox 101+, Safari 16+).
 *
 * The stylesheet hides the browser's own indicator and draws our calendar glyph
 * in its place (see `styles/date-fields.css`), which leaves exactly one path to
 * open the picker — this handler — so a click can never both open it natively
 * and through us.
 */

// Inputs whose picker is worth opening on a click. `text` is deliberately out:
// Vuetify's own v-date-picker menus are not native inputs and are untouched.
const PICKER_TYPES = new Set(['date', 'datetime-local', 'month', 'time', 'week'])

function openPicker(event) {
  const el = event.target
  if (!el || el.tagName !== 'INPUT') return
  if (!PICKER_TYPES.has(el.type)) return
  // A field nobody can edit must not pop a calendar.
  if (el.disabled || el.readOnly) return
  if (typeof el.showPicker !== 'function') return
  try {
    el.showPicker()
  } catch (e) {
    // showPicker() throws when the browser does not consider this a user
    // gesture, or when the picker is already open. Either way the field keeps
    // working as a plain text input, so this must stay silent.
  }
}

export const DateFieldPickerPlugin = {
  install() {
    // Capture phase: some fields sit inside components that stop propagation on
    // click, and the picker should still open for them.
    document.addEventListener('click', openPicker, true)
  },
}

export default DateFieldPickerPlugin
